import type { Connection, Edge, Node } from "@xyflow/react";
import { Maybe, Nothing } from "purify-ts";
import { filter, map } from "ramda";
import { match } from "ts-pattern";
import type { GetArrayElementType } from "widgets/utils";

import {
  type ActivityInstance,
  type InputActivityInstanceInput,
  type InputActivityParameterInput,
  type InputConnector,
  type InputValueWrapperInput,
  type InputWorkflowConfigInput,
  type InputWorkflowEventInput,
  type InputWorkflowInput,
  RetentionRuleEnum,
  type SupportedExecutorsEnum,
} from "@/graphql.ts";
import type {
  WorkflowActivityFragment,
  WorkflowConfigActivityFragment,
} from "@/modules/workflow/graphql/workflow.gql.ts";
import type { WorkflowEvent } from "@/modules/workflow/stores/workflow-editor-store-types.ts";
import type {
  Activity,
  BooleanParamType,
  DateParamType,
  EdgeData,
  EditorNodeData,
  FileParamType,
  NumberParamType,
  OrganismParamType,
  PreviewNodeData,
  RegionParamType,
  SampleParamType,
  StringParamType,
  TableParamType,
  TrackerParamType,
  WorkflowLoadData,
  WorkflowParameter,
} from "@/modules/workflow/utils/types";
import { workflowEventSchema } from "@/modules/workflow/utils/validationSchema.ts";

export interface WorkflowSaveInputData {
  id?: number | null;
  name: string;
  description?: string;
  userGroupId?: number;
}

export const generateWorkflowInputForSaving = (
  data: WorkflowSaveInputData,
  nodes: Node<EditorNodeData>[],
  edges: Edge<EdgeData>[],
  parametersRecord: Record<string, WorkflowParameter[]>,
  sampleTypeId?: number,
  defaultComputing?: SupportedExecutorsEnum | null,
): InputWorkflowInput => ({
  workflowId: data.id,
  name: data.name,
  description: data.description,
  usergroupId: data.userGroupId,
  config: generateWorkflowConfig(nodes, edges, parametersRecord, sampleTypeId, defaultComputing),
  // comments: ["not implemented!"],
});

export const generateWorkflowConfig = (
  nodes: Node<EditorNodeData>[],
  edges: Edge<EdgeData>[],
  parameters: Record<string, WorkflowParameter[]>,
  sampleTypeId?: number,
  defaultComputing?: SupportedExecutorsEnum | null,
  removeRestrictedValues = false,
): InputWorkflowConfigInput => ({
  activities: map<Node<EditorNodeData>, InputActivityInstanceInput>(
    (node): InputActivityInstanceInput => nodeToActivity(node, parameters[node.id] || [], removeRestrictedValues),
    filter((node) => node.type === "activity", nodes),
  ),
  connectors: map(edgeToConnector, edges),
  sampleTypeId,
  defaultExecutor: defaultComputing,
});

/**
 * [workflowEvent, nodes, edges, parameters, sampleTypeId] from the store
 */
export type AutoSaveData = [
  WorkflowEvent,
  Node<EditorNodeData>[],
  Edge<EdgeData>[],
  Record<string, WorkflowParameter[]>,
  number | undefined,
];

export const getWorkflowEventInputForSaving = (data: AutoSaveData) => {
  const [workflowEvent, nodes, edges, parameters, sampleTypeId] = data;

  if (workflowEvent.name == null || workflowEvent.projectId == null) {
    throw new Error("Session must have a name and a project must be selected");
  }

  const saveData = {
    config: generateWorkflowConfig(nodes, edges, parameters, sampleTypeId, workflowEvent.defaultComputing),
    description: workflowEvent.description ?? undefined,
    name: workflowEvent.name,
    projectId: workflowEvent.projectId,
    retentionRule: workflowEvent.retentionRule ?? RetentionRuleEnum.D,
    workflowEventId: workflowEvent.id,
    workflowId: workflowEvent.workflowId,
  } satisfies InputWorkflowEventInput;

  const output = workflowEventSchema.safeParse(saveData);

  return match(output)
    .with({ success: false }, ({ error }) => {
      throw error;
    })
    .with({ success: true }, ({ data }) => data)
    .exhaustive();
};

export const nodeToActivity = (
  node: Node<EditorNodeData>,
  parameters: WorkflowParameter[],
  removeRestrictedValues = false,
): InputActivityInstanceInput => ({
  disabled: node.data.disabled,
  identifier: node.id, // For edges connectors. Internal to reactflow.
  name: node.data.activityName,
  version: node.data.version,
  parameters: map<WorkflowParameter, InputActivityParameterInput>(
    (param) => ({
      identifier: param.parameterId,
      staged: param.staged,
      values: extractValue(param, removeRestrictedValues),
    }),
    parameters,
  ),
  metadata: {
    position: node.position,
    width: node?.measured?.width ?? node?.width ?? 150,
  },
  executor: node.data.computing,
});

export function getValueWithoutRestrictedResources<T>(param: WorkflowParameter): T | null {
  const values = param.values as Maybe<T>;
  let value: T | null = values.extractNullable();

  if (Maybe.isMaybe(values)) {
    value = param.schema?.safeParse(value).success ? value : null;
  }

  return value;
}

export const extractValue = (
  param: WorkflowParameter,
  removeRestrictedValues = false,
): InputValueWrapperInput | null => {
  if (removeRestrictedValues && getValueWithoutRestrictedResources(param) === null) {
    return null;
  }

  return match(param)
    .with({ values: Nothing }, () => null)
    .with({ typeName: "StringParam" }, () => ({
      textValue: (param.values as Maybe<StringParamType>).extractNullable(),
    }))
    .with({ typeName: "NumberParam" }, () => ({
      numberValue: (param.values as Maybe<NumberParamType>).extractNullable(),
    }))
    .with({ typeName: "BooleanParam" }, () => ({
      booleanValue: (param.values as Maybe<BooleanParamType>).extractNullable(),
    }))
    .with({ typeName: "GenomeParam" }, () => ({
      organismId: (param.values as Maybe<OrganismParamType>).extractNullable(),
    }))
    .with({ typeName: "FileParam" }, () => ({
      fileId: (param.values as Maybe<FileParamType>).extractNullable(),
    }))
    .with({ typeName: "SampleParam" }, () => {
      return {
        sampleValue: (param.values as Maybe<SampleParamType>).extractNullable(),
      };
    })
    .with({ typeName: "RegionParam" }, () => ({
      region: (param.values as Maybe<RegionParamType>).extractNullable(),
    }))
    .with({ typeName: "DateParam" }, () => ({
      dateValue: (param.values as Maybe<DateParamType>).extractNullable(),
    }))
    .with({ typeName: "TrackerParam" }, () => ({
      attributeId: (param.values as Maybe<TrackerParamType>).extractNullable(),
    }))
    .with({ typeName: "TableParam" }, () => ({
      viewId: (param.values as Maybe<TableParamType>).extractNullable(),
    }))
    .exhaustive();
};

export const edgeToConnector = (edge: Edge<EdgeData>): InputConnector => ({
  source: edge.source, // activity.metadata.id
  output: edge.sourceHandle, // activity.output.id
  target: edge.target, // activity.metadata.id
  input: edge.targetHandle, // activity.input.id
});

export const connectorToEdge = (
  connector: GetArrayElementType<WorkflowLoadData["config"]["connectors"]>,
  isDisabled?: boolean,
): Connection | Edge => ({
  source: connector.source,
  sourceHandle: connector.output,
  target: connector.target,
  targetHandle: connector.input,
  type: "activity", // Reactflow internals: We don't support other types of edges yet
  data: {
    // we use it only for the preview in the session listing (no store context)
    // i.e. we can safely set data to undefined if not explicitly provided
    disabled: isDisabled ?? false,
  },
});

export const activityToEditorNode = (workflowActivity: WorkflowConfigActivityFragment): Node<EditorNodeData> => ({
  id: workflowActivity.identifier,
  data: {
    activityName: workflowActivity.name,
    version: workflowActivity.version,
    status: workflowActivity.status ?? undefined,
    outputFolderId: workflowActivity.outputFolder?.id,
    computing: workflowActivity.executor ?? undefined,
    disabled: workflowActivity.disabled ?? undefined,
    // ...
  } satisfies EditorNodeData,
  position: workflowActivity.metadata.position,
  // FIXME Not sure about this usage, as it's not passed as props weirdly
  measured: { width: workflowActivity.metadata.width ?? 150 },
  type: "activity", // Reactflow internals: Versus "comment" type node for instance
});

export type WorkflowActivityWithStatus = WorkflowActivityFragment & {
  metadata: WorkflowActivityFragment["metadata"] & { status?: PreviewNodeData["status"] };
};

export const activityToPreviewNode = (
  workflowActivity: ActivityInstance,
  activity?: Activity,
): Node<PreviewNodeData> => ({
  id: workflowActivity.identifier,
  data: {
    activity,
    status: workflowActivity.status ?? undefined,
    width: workflowActivity.metadata.width ?? 150,
    // ...
    staged: workflowActivity?.parameters?.some((param) => param.staged),
    disabled: workflowActivity?.disabled ?? false,
  },
  position: workflowActivity.metadata.position,
  type: "activity", // Reactflow internals: Versus "comment" type node for instance
  measured: { width: workflowActivity.metadata.width ?? 150 },
});
